import React from "react";
import {
  Box,
  Button,
  Popover,
  SpaceBetween,
} from "@amzn/awsui-components-console";
import {
  FixedFeeConfig,
  SimplifiedFixedFeeConfig,
} from "@amzn/fuse-accounting-central-datastore-client";
import { isProd } from "../../util/is-prod";

const PTP_PROD_BASE_URL = "https://app.amazonfuse.com";
const PTP_BETA_BASE_URL = "https://beta.app.amazonfuse.com";

/**
 * Builds the Partner Portal (PTP) URL for a given FixedFeeConfig or SimplifiedFixedFeeConfig.
 *
 * Logic: If dealConfig.fuseOffers is non-empty, grab the contractId from the
 * first offer. The PTP link is then:
 *   <base>/partner-management/partners/<contractId>/fixed-fee/<fixedFeeId>
 *
 * Returns undefined if fuseOffers is empty or missing, or if fixedFeeId is missing.
 */
const getPartnerPortalUrl = (
  fixedFeeConfig: FixedFeeConfig | SimplifiedFixedFeeConfig,
): string | undefined => {
  const fuseOffers = fixedFeeConfig.dealConfig?.fuseOffers;
  if (!fuseOffers || fuseOffers.length === 0) return undefined;

  const contractId = fuseOffers[0].contractId;
  if (!contractId || !fixedFeeConfig.fixedFeeId) return undefined;

  const baseUrl = isProd() ? PTP_PROD_BASE_URL : PTP_BETA_BASE_URL;
  return `${baseUrl}/partner-management/partners/${contractId}/fixed-fee/${fixedFeeConfig.fixedFeeId}`;
};

interface PartnerPortalLinkProps {
  fixedFeeConfig?: FixedFeeConfig | SimplifiedFixedFeeConfig;
}

/**
 * Renders the "View in Partner Portal" button + info icon.
 *
 * - If fixedFeeConfig is undefined (no selection): button disabled, no tooltip.
 * - If fixedFeeConfig has no fuseOffers: button disabled, hover tooltip explains why.
 * - If fuseOffers exist: button enabled, links to PTP.
 * - Info icon always shows the accuracy disclaimer on click.
 */
export const PartnerPortalLink: React.FC<PartnerPortalLinkProps> = ({
  fixedFeeConfig,
}) => {
  const ptpUrl = fixedFeeConfig
    ? getPartnerPortalUrl(fixedFeeConfig)
    : undefined;
  const hasNoOffer = fixedFeeConfig && !ptpUrl;

  const ptpButton = (
    <Button
      variant="normal"
      iconName="external"
      href={ptpUrl ?? ""}
      target="_blank"
      disabled={!ptpUrl}
    >
      View in Partner Portal
    </Button>
  );

  return (
    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
      {hasNoOffer ? (
        <span title="No associated Fuse Offer, unable to link to Partner Portal">
          {ptpButton}
        </span>
      ) : (
        ptpButton
      )}
      <Popover
        renderWithPortal
        dismissButton={false}
        position="bottom"
        size="medium"
        triggerType="custom"
        content={
          <Box color="text-body-secondary">
            Associated Offers section in Partner Portal might not be accurate,
            if offers across different contractIds are onboarded to the same
            config.
          </Box>
        }
      >
        <Button variant="inline-icon" iconName="status-info" />
      </Popover>
    </SpaceBetween>
  );
};
